import { SessionAnsweringStrategy } from '@lemonade-hq/chat-engine';
import type { SchemaData, SerializedSessionWithHistory } from '@lemonade-hq/chat-engine';
import { Client } from '@lemonade-hq/nuiza';
import type { AxiosRequestConfig } from 'axios';
import { appendQueryParams, createTrackingHeaders, extractNamespaceFromSubdomain, extractOriginInfo } from './utils';
import type { BaseClientData } from './utils';

export { extractOriginInfo, extractNamespaceFromSubdomain };

export type ChatEngineSession<TPresentation = undefined> = SerializedSessionWithHistory<TPresentation>;

export interface ChatEngineInitSessionRequestPayload {
  readonly scriptName: string;
  readonly sessionId?: string | null;
  readonly stepName?: string;
  readonly forceRestart?: boolean;
  readonly initData?: Record<string, unknown>;
}

export interface ChatEngineSubmitAnswersRequestPayload {
  readonly scriptName: string;
  readonly sessionId: string;
  readonly stepId: string;
  readonly answers: SchemaData;
  readonly resync?: boolean;
  readonly skipHooks?: boolean;
}

type ChatEngineClientData = BaseClientData;

export class ChatEngineClient<TPresentation> {
  constructor(private readonly chatEngineClientData: ChatEngineClientData) {}

  async initSession(
    { scriptName, sessionId, initData, forceRestart, stepName }: ChatEngineInitSessionRequestPayload,
    options?: AxiosRequestConfig,
  ): Promise<ChatEngineSession<TPresentation>> {
    const { serverBaseUrl, accessTokenOverride, emailCode } = this.chatEngineClientData;

    const trackingHeaders = createTrackingHeaders();

    if (sessionId != null) {
      const path = new URL(`${serverBaseUrl}/chat/public/scripts/${scriptName}/sessions/${sessionId}`);

      return await Client.get<ChatEngineSession<TPresentation>>(
        appendQueryParams(
          path,
          accessTokenOverride,
          emailCode,
          stepName != null && stepName !== '' ? { stepName } : undefined,
        ),
        { headers: trackingHeaders },
      );
    }

    const path = new URL(`${serverBaseUrl}/chat/public/scripts/${scriptName}/sessions`);

    return await Client.post<ChatEngineSession<TPresentation>>(
      appendQueryParams(
        path,
        accessTokenOverride,
        emailCode,
        stepName != null && stepName !== '' ? { stepName } : undefined,
      ),
      {
        ...(initData != null && Object.keys(initData).length > 0 ? { data: { ...initData } } : {}),
        forceRestart: forceRestart ?? false,
      },
      // @ts-expect-error: need to upg nuiza client
      { ...options, headers: { ...trackingHeaders, ...options?.headers } },
    );
  }

  async submitAnswers(
    { scriptName, sessionId, stepId, answers, resync, skipHooks }: ChatEngineSubmitAnswersRequestPayload,
    options?: AxiosRequestConfig,
  ): Promise<ChatEngineSession<TPresentation>> {
    const { serverBaseUrl, accessTokenOverride } = this.chatEngineClientData;

    const trackingHeaders = createTrackingHeaders();

    const path = new URL(`${serverBaseUrl}/chat/public/scripts/${scriptName}/sessions/${sessionId}/steps/${stepId}`);
    return await Client.post<ChatEngineSession<TPresentation>>(
      appendQueryParams(path, accessTokenOverride),
      {
        answers,
        ...(resync ? { strategy: SessionAnsweringStrategy.Resync } : {}),
        ...(skipHooks != null && { skipHooks }),
      },
      // @ts-expect-error: need to upg nuiza client
      { ...options, headers: { ...trackingHeaders, ...options?.headers } },
    );
  }
}
